import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { MicrositeSession, MicrositeSessionResult } from 'shared';
import { micrositeApi, micrositeTokenStore, ApiError } from '../lib/api';
import { Icon } from '../components/icons';
import { MicrositeIdentity } from './Identity';
import { MicrositeDrives } from './Drives';
import { MicrositeServices } from './Services';
import { MicrositeHistory } from './History';

interface Ctx {
  session: MicrositeSession;
  /** Replace the session after registering, linking, or joining a drive. */
  setSession: (s: MicrositeSession) => void;
  reload: () => Promise<void>;
}

const MicrositeCtx = createContext<Ctx | null>(null);

export function useMicrosite(): Ctx {
  const c = useContext(MicrositeCtx);
  if (!c) throw new Error('useMicrosite dipakai di luar MicrositeApp');
  return c;
}

/**
 * Whether the donor sections (Kegiatan, Riwayat, Profil) are shown.
 *
 * Off at the client's request (15 Sep 2026): the microsite currently offers only Layanan.
 * The donor screens are kept intact rather than deleted, so turning them back on is this
 * one flag.
 */
const DONOR_SECTIONS_ENABLED = false;

/** Sections AiYO may deep-link to with `?route=`. */
const ROUTE_HINTS: Record<string, string> = {
  kegiatan: '/microsite',
  layanan: '/microsite/layanan',
  riwayat: '/microsite/riwayat',
  profil: '/microsite/profil',
  // The reference app's own names, mapped to the nearest equivalent here.
  membership: '/microsite/profil',
  match: '/microsite',
  // Aliases AiYO is likely to deep-link the service catalogue by. An urgent request
  // should land on the catalogue rather than on a notice saying the section is unknown.
  services: '/microsite/layanan',
  ambulans: '/microsite/layanan',
};

type Phase =
  | { kind: 'loading' }
  | { kind: 'ready'; session: MicrositeSession }
  | { kind: 'no-session' }
  | { kind: 'not-configured' }
  | { kind: 'error'; message: string };

/**
 * The donor-facing microsite.
 *
 * AiYO opens `/microsite?token=<SSO_TOKEN>[&route=...]`. The token is exchanged ONCE for
 * a session of our own, then stripped from the URL so it cannot linger in browser history
 * or leak through a shared link.
 *
 * This surface deliberately shares nothing with the backoffice shell but the design
 * tokens: a donor has no sidebar, no admin session, and no route worth guarding.
 */
export function MicrositeApp() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  /** The service tab stands apart: it is the one section that needs no donor record. */
  const onServices = pathname.startsWith('/microsite/layanan');
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [unknownRoute, setUnknownRoute] = useState<string | null>(null);
  /** The exchange must happen once. React StrictMode double-invokes effects in dev. */
  const bootstrapped = useRef(false);

  const load = useCallback(async () => {
    setPhase({ kind: 'ready', session: await micrositeApi.get<MicrositeSession>('/session') });
  }, []);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    (async () => {
      const params = new URLSearchParams(window.location.search);
      const ssoToken = params.get('token');
      const routeHint = params.get('route');

      /** Consume the query string, keeping any params that are not ours. */
      const stripToken = () => {
        params.delete('token');
        params.delete('route');
        const rest = params.toString();
        window.history.replaceState(
          {}, '',
          window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash,
        );
      };

      // Consume our query params in every branch that has a session to go to, not just
      // after an exchange: `?route=` left in the URL would re-navigate on a reload.
      if (ssoToken || micrositeTokenStore.get()) stripToken();

      try {
        if (ssoToken) {
          const res = await micrositeApi.post<MicrositeSessionResult>('/auth/session', { token: ssoToken });
          micrositeTokenStore.set(res.token);
          setPhase({ kind: 'ready', session: res.session });
        } else if (micrositeTokenStore.get()) {
          await load();
        } else {
          // Nothing to work with. Whether the integration is even switched on decides
          // which of the two honest messages to show.
          const status = await micrositeApi.get<{ configured: boolean }>('/status')
            .catch(() => ({ configured: true }));
          setPhase({ kind: status.configured ? 'no-session' : 'not-configured' });
          return;
        }

        if (routeHint) {
          const target = ROUTE_HINTS[routeHint];
          // An unrecognised section is reported, not silently swallowed — AiYO changing a
          // route name should be visible rather than landing donors somewhere arbitrary.
          if (target) navigate(target, { replace: true });
          else setUnknownRoute(routeHint);
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          micrositeTokenStore.clear();
          setPhase({ kind: 'no-session' });
          return;
        }
        if (err instanceof ApiError && err.status === 503) {
          setPhase({ kind: 'not-configured' });
          return;
        }
        setPhase({
          kind: 'error',
          message: err instanceof ApiError ? err.message : 'Gagal memuat sesi microsite.',
        });
      }
    })();
  }, [load, navigate]);

  if (phase.kind === 'loading') {
    return <MicrositeNotice title="Memuat…" body="Menyiapkan sesi Anda." />;
  }

  if (phase.kind === 'no-session') {
    return (
      <MicrositeNotice
        title="Buka dari aplikasi AiYO"
        body={'Halaman ini hanya bisa dibuka lewat aplikasi AiYO, karena identitas Anda '
          + 'diverifikasi di sana. Silakan kembali ke aplikasi dan pilih menu donor darah.'}
      />
    );
  }

  if (phase.kind === 'not-configured') {
    return (
      <MicrositeNotice
        title="Layanan belum aktif"
        body={'Integrasi microsite belum dikonfigurasi. Hubungi admin AiYO — tidak ada '
          + 'yang perlu Anda lakukan dari sisi ini.'}
      />
    );
  }

  if (phase.kind === 'error') {
    return <MicrositeNotice title="Ada gangguan" body={phase.message} tone="error" />;
  }

  const { session } = phase;

  return (
    <MicrositeCtx.Provider
      value={{
        session,
        setSession: (s) => setPhase({ kind: 'ready', session: s }),
        reload: load,
      }}
    >
      <div className="ms">
        <header className="ms-head">
          <div className="ms-brand">
            <span className="ms-mark"><Icon name="droplet" size={18} /></span>
            <div>
              <strong>Donor Darah PMI</strong>
              <span className="ms-who">{session.profile.name}</span>
            </div>
          </div>
        </header>

        {/* With only Layanan left, a one-tab bar would be a control that does nothing. */}
        {DONOR_SECTIONS_ENABLED && (
          <nav className="ms-tabs" aria-label="Bagian microsite">
            <NavLink to="/microsite" end className={({ isActive }) => (isActive ? 'ms-tab is-on' : 'ms-tab')}>
              <Icon name="calendar" size={18} />
              <span>Kegiatan</span>
            </NavLink>
            <NavLink to="/microsite/layanan" className={({ isActive }) => (isActive ? 'ms-tab is-on' : 'ms-tab')}>
              <Icon name="ambulance" size={18} />
              <span>Layanan</span>
            </NavLink>
            <NavLink to="/microsite/riwayat" className={({ isActive }) => (isActive ? 'ms-tab is-on' : 'ms-tab')}>
              <Icon name="clipboard" size={18} />
              <span>Riwayat</span>
            </NavLink>
            <NavLink to="/microsite/profil" className={({ isActive }) => (isActive ? 'ms-tab is-on' : 'ms-tab')}>
              <Icon name="idCard" size={18} />
              <span>Profil</span>
            </NavLink>
          </nav>
        )}

        <main className="ms-body">
          {unknownRoute && (
            <div className="msg error">
              Bagian "{unknownRoute}" tidak dikenali, jadi kami tampilkan daftar
              {DONOR_SECTIONS_ENABLED ? ' kegiatan' : ' layanan'}.
              <button type="button" style={{ marginLeft: '0.6rem' }} onClick={() => setUnknownRoute(null)}>
                Mengerti
              </button>
            </div>
          )}

          {/*
            Scoped to the drive tabs. A service request needs NO donor record — that is
            the whole reason `form_submission` accepts two subjects — so telling somebody
            requesting an ambulance to register as a donor first is both wrong and, for
            an urgent request, actively harmful.
          */}
          {DONOR_SECTIONS_ENABLED && !session.registered && !onServices && (
            <div className="msg error">
              Lengkapi data pendonor Anda di tab <strong>Profil</strong> sebelum mendaftar
              kegiatan.
            </div>
          )}

          {DONOR_SECTIONS_ENABLED ? (
            <Routes>
              <Route index element={<MicrositeDrives />} />
              <Route path="layanan" element={<MicrositeServices />} />
              <Route path="riwayat" element={<MicrositeHistory />} />
              <Route path="profil" element={<MicrositeIdentity />} />
              <Route path="*" element={<MicrositeDrives />} />
            </Routes>
          ) : (
            // Every path, including an old deep link to a donor section, lands on Layanan.
            <Routes>
              <Route path="*" element={<MicrositeServices />} />
            </Routes>
          )}
        </main>
      </div>
    </MicrositeCtx.Provider>
  );
}

function MicrositeNotice({ title, body, tone }: { title: string; body: string; tone?: 'error' }) {
  return (
    <div className="ms ms-centered">
      <div className="card stack" style={{ maxWidth: 460 }}>
        <div className="ms-brand">
          <span className="ms-mark"><Icon name="droplet" size={18} /></span>
          <strong>Donor Darah PMI</strong>
        </div>
        <h2 style={{ fontSize: '1.2rem' }}>{title}</h2>
        <p className={tone === 'error' ? 'msg error' : 'muted'} style={{ margin: 0, fontSize: 14 }}>
          {body}
        </p>
      </div>
    </div>
  );
}
